import { zodResolver } from '@hookform/resolvers/zod';
import { modelUtils } from '@pelicargo/utils';
import { FieldValues, Resolver, ResolverOptions } from 'react-hook-form';
import { z } from 'zod';

import { TrpcOutputs } from '../../config/trpc';
import { pruneEmpty } from './prune';
import { ThemeSectionName } from './sections';

const { CUSTOMER_ASSET_SLOT_KEYS, CUSTOMER_COPY_KEYS, CustomerThemeSchema } = modelUtils.customerTheme;

/** Fully-populated shape the query returns: every section is present, so no leaf needs a guard. */
export type CustomerTheme = TrpcOutputs['getTenantCustomerTheme'];

/** What gets serialised and sent. Unlike `CustomerTheme` every section is optional here, which is
 *  the whole point — an omitted section is an omitted override. */
export type ThemePayload = z.input<typeof CustomerThemeSchema>;

/** Same tree, but every leaf is a plain string: the controls are text inputs and `''` is how the
 *  form spells "no override". `pruneEmpty` is what turns that back into an absent key. */
type Overridable<T> = { [K in keyof T]?: NonNullable<T[K]> extends object ? Overridable<NonNullable<T[K]>> : string };

export type ThemeFormValues = Required<Overridable<Omit<CustomerTheme, 'copy' | 'assets'>>> & {
  /**
   * Parallel to `CUSTOMER_COPY_KEYS` / `CUSTOMER_ASSET_SLOT_KEYS`. The copy keys contain dots
   * (`auth.panel.heading`) and react-hook-form reads a dot in a field name as nesting — it has no
   * escape for a literal one — so the key cannot be the field name. The index is the alias, and
   * `toThemePayload` puts the real keys back.
   */
  copyOverrides: string[];
  assetOverrides: string[];
};

const toText = (value?: string | number) => (value === undefined || value === null ? '' : String(value));

const toTextRecord = (values?: Record<string, string | number>) => {
  const entries = Object.entries(values ?? {}).map(([key, value]) => [key, toText(value)]);

  return Object.fromEntries(entries);
};

/**
 * Seeds the form from a theme. Every nested object is written even when it is empty, and that is
 * not cosmetic: react-hook-form's `set()` replaces a *missing* parent with an array when the next
 * path segment is numeric, so registering `colors.primary.500` against an absent `colors.primary`
 * would build a 501-slot array instead of an object and the payload would fail validation.
 */
export const toFormValues = (theme?: CustomerTheme | null): ThemeFormValues => ({
  colors: { primary: toTextRecord(theme?.colors?.primary), secondary: toTextRecord(theme?.colors?.secondary) },
  semantic: toTextRecord(theme?.semantic),
  fonts: toTextRecord(theme?.fonts),
  radii: toTextRecord(theme?.radii),
  // Listed leaf by leaf rather than spread, because `heading` mixes scalars with the h1…h6 objects.
  heading: {
    fontFamily: toText(theme?.heading?.fontFamily),
    fontWeight: toText(theme?.heading?.fontWeight),
    letterSpacing: toText(theme?.heading?.letterSpacing),
    textTransform: toText(theme?.heading?.textTransform),
    color: toText(theme?.heading?.color),
    h1: toTextRecord(theme?.heading?.h1),
    h2: toTextRecord(theme?.heading?.h2),
    h3: toTextRecord(theme?.heading?.h3),
    h4: toTextRecord(theme?.heading?.h4),
    h5: toTextRecord(theme?.heading?.h5),
    h6: toTextRecord(theme?.heading?.h6),
  },
  button: toTextRecord(theme?.button),
  field: toTextRecord(theme?.field),
  input: toTextRecord(theme?.input),
  label: toTextRecord(theme?.label),
  switch: toTextRecord(theme?.switch),
  icon: toTextRecord(theme?.icon),
  logo: toTextRecord(theme?.logo),
  copyOverrides: CUSTOMER_COPY_KEYS.map((key) => toText(theme?.copy?.[key])),
  assetOverrides: CUSTOMER_ASSET_SLOT_KEYS.map((slot) => toText(theme?.assets?.[slot])),
});

const DIGITS_ONLY = /^-?\d+(\.\d+)?$/;

/**
 * `logo.*` is `z.union([z.number(), cssValue])` and is applied as a Chakra layout prop, where a
 * number means px but a bare numeric string does not (`width="80"` is not valid CSS), so a
 * digits-only entry is sent as a number and anything else passes through as CSS.
 */
const toDimension = (value?: string) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  return DIGITS_ONLY.test(trimmed) ? Number(trimmed) : trimmed;
};

/** Form shape -> wire shape, with every "no override" key removed. */
export const toThemePayload = (values: ThemeFormValues): ThemePayload => {
  const { copyOverrides, assetOverrides, logo, ...sections } = values;

  const raw = {
    ...sections,
    logo: {
      width: toDimension(logo?.width),
      height: toDimension(logo?.height),
      fullWidth: toDimension(logo?.fullWidth),
      fullHeight: toDimension(logo?.fullHeight),
    },
    copy: Object.fromEntries(CUSTOMER_COPY_KEYS.map((key, index) => [key, copyOverrides?.[index]])),
    assets: Object.fromEntries(CUSTOMER_ASSET_SLOT_KEYS.map((slot, index) => [slot, assetOverrides?.[index]])),
  };

  return (pruneEmpty(raw) as ThemePayload | undefined) ?? {};
};

/** `Configuration.value` is a scalar string, so the stored form is JSON text. */
export const toThemeJson = (values: ThemeFormValues) => JSON.stringify(toThemePayload(values));

const FORM_KEY_BY_SECTION: Record<ThemeSectionName, keyof ThemeFormValues> = {
  colors: 'colors',
  semantic: 'semantic',
  fonts: 'fonts',
  radii: 'radii',
  heading: 'heading',
  button: 'button',
  field: 'field',
  input: 'input',
  label: 'label',
  switch: 'switch',
  icon: 'icon',
  logo: 'logo',
  copy: 'copyOverrides',
  assets: 'assetOverrides',
};

/**
 * The current values with one section blanked. `setValue(section, {})` cannot do this — it walks
 * the *new* object's keys, so an empty one clears nothing — hence a whole-form value instead.
 */
export const clearSection = (values: ThemeFormValues, section: ThemeSectionName): ThemeFormValues => {
  const key = FORM_KEY_BY_SECTION[section];
  const empty = toFormValues(null);

  // The computed key defeats inference; the value comes from `toFormValues` so the shape is right.
  return { ...values, ...({ [key]: empty[key] } as Partial<ThemeFormValues>) };
};

const validatePayload = zodResolver(CustomerThemeSchema);

/**
 * Validates with the same `CustomerThemeSchema` the server re-validates with, but against the
 * *payload* — after the empty fields are stripped — so a field the user deliberately left blank is
 * never reported as an invalid empty string. `values` is returned unchanged rather than zod's
 * parsed output because `handleSubmit` passes whatever a resolver returns straight to the submit
 * handler, and the parsed output has the payload shape, not the form shape.
 */
export const themeFormResolver: Resolver<ThemeFormValues> = async (values, context, options) => {
  const { errors } = await validatePayload(
    toThemePayload(values),
    context,
    options as unknown as ResolverOptions<FieldValues>,
  );

  if (Object.keys(errors).length > 0) return { values: {}, errors };

  return { values, errors: {} };
};

// tRPC serialises an input-validation ZodError into `error.message` as a JSON array of issues.
const trpcIssuesSchema = z.array(z.object({ message: z.string(), path: z.array(z.coerce.string()) }));

export const toFieldIssues = (error: unknown) => {
  const message = (error as { message?: string })?.message;
  if (!message) return [];

  const parsed = trpcIssuesSchema.safeParse(tryParseJson(message));

  return parsed.success ? parsed.data : [];
};

const tryParseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * Server issues arrive under the `value` path (`value.colors.primary.500`), because the mutation
 * takes the theme as a JSON string. Maps one back onto the field that produced it, or returns
 * undefined when no field owns it — those stay in the toast rather than being attached anywhere.
 */
export const toFormPath = (path: string[]): string | undefined => {
  const [head, section, key] = path;
  if (head !== 'value' || !section) return undefined;

  if (section !== 'copy' && section !== 'assets') return path.slice(1).join('.');

  const keys: string[] = section === 'copy' ? [...CUSTOMER_COPY_KEYS] : [...CUSTOMER_ASSET_SLOT_KEYS];
  const index = keys.indexOf(key);
  if (index < 0) return undefined;

  return `${FORM_KEY_BY_SECTION[section]}.${index}`;
};
